import { getImage, storeImage } from './imageSessionCache';
import { MAX_FIRESTORE_MSG_BYTES } from './config';

const KIND_LABELS = {
  pdf: 'PDF',
  word: 'Word',
  excel: 'Excel',
  ppt: 'PowerPoint',
  image: 'Image',
  text: 'Text',
  file: 'File',
};

export function getAttachmentKind(attach) {
  if (!attach) return 'file';
  if (attach.kind) return attach.kind;
  if (attach.type === 'image') return 'image';
  if (attach.type === 'pdf') return 'pdf';
  const n = (attach.fileName || '').toLowerCase();
  if (/\.(docx?|doc)$/.test(n)) return 'word';
  if (/\.(xlsx?|xls)$/.test(n)) return 'excel';
  if (/\.(pptx?|ppt)$/.test(n)) return 'ppt';
  if (/\.(txt|csv|json|md)$/.test(n)) return 'text';
  return 'file';
}

export function getAttachmentLabel(kind) {
  return KIND_LABELS[kind] || 'File';
}

export function kindFromFileName(fileName) {
  const n = (fileName || '').toLowerCase();
  if (n.endsWith('.pdf')) return 'pdf';
  if (/\.(docx?)$/.test(n)) return 'word';
  if (/\.(xlsx?|xls)$/.test(n)) return 'excel';
  if (/\.(pptx?)$/.test(n)) return 'ppt';
  if (/\.(txt|csv|json|md)$/.test(n)) return 'text';
  return 'file';
}

export function attachmentSummaryLine(attach) {
  const kind = getAttachmentKind(attach);
  const name = attach.fileName || 'file';
  return `User attached ${getAttachmentLabel(kind)}: ${name}`;
}

export function getExtractedText(attach) {
  if (!attach) return '';
  if (attach.extractedText) return attach.extractedText;
  if (attach.type === 'pdf' && attach.content) return attach.content;
  const c = attach.content || attach.excerpt || '';
  return c.replace(/^Attached [^:]+:\n?/i, '').trim();
}

export function resolveAttachmentsForApi(attachments) {
  return (attachments || []).map((a) => {
    if (a.type === 'image' && a.imageId) {
      const cached = getImage(a.imageId);
      if (cached) {
        return { ...a, imageData: cached.imageData, preview: cached.preview };
      }
    }
    if (a.type === 'image' && (a.imageData || a.preview || a.content)) return a;
    return a;
  });
}

export function resolveAttachmentsForDisplay(attachments) {
  return (attachments || []).map((a) => {
    if (a.type !== 'image') return a;
    const fromStore = a.imageData || a.preview;
    if (fromStore) {
      if (a.imageId) {
        storeImage(a.imageId, {
          imageData: a.imageData || a.preview,
          preview: a.preview || a.imageData,
        });
      }
      return { ...a, preview: a.preview || a.imageData, imageData: a.imageData || a.preview };
    }
    if (a.imageId) {
      const cached = getImage(a.imageId);
      if (cached) {
        return { ...a, preview: cached.preview, imageData: cached.imageData };
      }
    }
    return a;
  });
}

export function attachmentToApiParts(attach) {
  const parts = [];
  const name = attach.fileName || 'file';
  const kind = getAttachmentKind(attach);

  if (attach.type === 'image') {
    const url = attach.imageData || attach.preview || attach.content;
    if (url) parts.push({ type: 'image_url', image_url: { url } });
    return parts;
  }

  if (attach.type === 'pdf') {
    const text = getExtractedText(attach) || attach.excerpt || '';
    parts.push({ type: 'text', text: `Attached PDF (${name}):\n${text}` });
    return parts;
  }

  if (attach.type === 'doc' || kind !== 'file') {
    const text = getExtractedText(attach) || attach.excerpt || '';
    parts.push({
      type: 'text',
      text: `Attached ${getAttachmentLabel(kind)} file (${name}):\n${text}`,
    });
    return parts;
  }

  return parts;
}

export function buildUserApiContent(text, attachments, searchContext = '') {
  const parts = [];
  const trimmed = (text || '').trim();
  if (trimmed) parts.push({ type: 'text', text: trimmed + searchContext });
  else if (searchContext) parts.push({ type: 'text', text: searchContext.trim() });
  const resolved = resolveAttachmentsForApi(attachments);
  resolved.forEach((a) => parts.push(...attachmentToApiParts(a)));
  if (parts.length === 0) return '';
  if (parts.length === 1 && parts[0].type === 'text') return parts[0].text;
  return parts;
}

export function sanitizeAttachmentForStorage(attach) {
  const kind = getAttachmentKind(attach);
  const base = {
    type: attach.type,
    fileName: attach.fileName,
    kind,
    attachmentSummary: attachmentSummaryLine(attach),
  };
  if (attach.type === 'image') {
    const cached = attach.imageId ? getImage(attach.imageId) : null;
    const imageData = attach.imageData || cached?.imageData || null;
    const preview = attach.preview || cached?.preview || imageData || null;
    return {
      ...base,
      imageId: attach.imageId || null,
      preview,
      imageData,
    };
  }
  const excerpt = getExtractedText(attach).slice(0, 4000);
  return { ...base, excerpt };
}

export function needsVisionModel(attachments) {
  return resolveAttachmentsForApi(attachments).some((a) => a.type === 'image' && (a.imageData || a.preview));
}

export function messageToApiContent(msg) {
  if (!msg) return '';
  let text = msg.text || '';
  const attachments = msg.attachments || [];
  if (!text.trim() && attachments.length) {
    const summaries = attachments
      .map((a) => a.attachmentSummary || attachmentSummaryLine(a))
      .filter(Boolean);
    if (summaries.length) text = summaries.join('\n');
  }
  return buildUserApiContent(text, attachments);
}

export function prepareMessageForFirestore(msg) {
  const { id: _localId, isStreaming: _streaming, ...msgWithoutLocalId } = msg;
  const attachments = (msgWithoutLocalId.attachments || []).map(sanitizeAttachmentForStorage);
  let prepared = { ...msgWithoutLocalId, attachments };
  let size = JSON.stringify(prepared).length;
  if (size <= MAX_FIRESTORE_MSG_BYTES) return prepared;

  prepared = {
    ...msgWithoutLocalId,
    attachments: attachments.map((a) => {
      if (a.excerpt) return { ...a, excerpt: a.excerpt.slice(0, 1500) };
      return a;
    }),
  };
  size = JSON.stringify(prepared).length;
  if (size <= MAX_FIRESTORE_MSG_BYTES) return prepared;

  return {
    ...msgWithoutLocalId,
    text: (msg.text || '').slice(0, 5000),
    attachments: attachments.map((a) => ({
      type: a.type,
      fileName: a.fileName,
      kind: a.kind,
      attachmentSummary: a.attachmentSummary,
      imageId: a.imageId || null,
      preview: a.preview || null,
      imageData: a.imageData || null,
    })),
  };
}
